import { retry } from 'services/utilityService';

import { apiGetUsersUuids } from './apiGetUsersUuids';

/**
 * Resolves a batch of user PKs to UUIDs with automatic retry/backoff on transient Cloud failures.
 *
 * Uses the shared `retry` helper with its default success check (Cloud `status`), since the
 * `legacy_id_mapping` response carries a status in its body. Throws on a terminal request failure
 * (network / error status) and on an unexpected response shape, so the caller can treat the batch as
 * unresolved and fall back to cached pairs. Unmapped ids come back as `null` inside the returned map
 * — that is a valid, expected case, not an error.
 *
 * @param {Array<string|number>} ids - User PKs (PK_User) to resolve. At most 200 per call.
 * @returns {Promise<Object>} Map of PK_User → UUID (values may be null for unmapped ids).
 * @throws {Error} If the request ultimately fails or the response shape is unexpected.
 */
export const getUsersUuidsWithRetry = async (ids) => {
    const response = await retry(() => apiGetUsersUuids(ids));
    const idsToUuids = response?.data?.data?.map?.user?.ids_to_uuids;

    if (!idsToUuids || typeof idsToUuids !== 'object') {
        throw new Error('Unexpected legacy_id_mapping response format: "ids_to_uuids" is missing');
    }

    return idsToUuids;
};
